import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ExternalLink, ChevronDown, GraduationCap, Award, BookOpen } from 'lucide-react';

const LOGO_URL = "/logo.png";
const LMS_URL = "https://lms.gihds.org";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 50);
  }, [setIsScrolled]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [setActiveDropdown]);

  const academicsDropdown = {
    name: 'Academics',
    sections: [
      {
        title: 'Degree Programs',
        icon: GraduationCap,
        items: [
          { name: "Master of Humanitarian Action & Development", path: '/program/masters' },
          { name: "Master of Development Project & Program Management", path: '/program/masters-ppm' },
          { name: 'Doctoral Program', path: '/program/doctoral' },
        ]
      },
      {
        title: 'Certificate Courses',
        icon: Award,
        items: [
          { name: 'AI in Humanitarian Action & Development', path: '/certificate/ai-humanitarian' },
        ]
      },
      {
        title: 'Resources',
        icon: BookOpen,
        items: [
          { name: 'All Courses', path: '/courses' },
          { name: 'Scholarship Info', path: '/scholarship' },
        ]
      }
    ]
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Academics', dropdown: true },
    { name: 'Admissions', path: '/admissions' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  const headerBg = isScrolled || !isHomePage 
    ? 'bg-white shadow-sm' 
    : 'bg-gradient-to-b from-black/70 via-black/40 to-transparent';
  
  const textColor = isScrolled || !isHomePage 
    ? 'text-slate-900' 
    : 'text-white';

  const handleDropdownClick = (e, name) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === name ? null : name);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBg}`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3"
            data-testid="header-logo"
          >
            <img 
              src={LOGO_URL} 
              alt="Geneva Institute of Humanitarian & Development Studies" 
              className="h-10 sm:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              link.dropdown ? (
                <div key={link.name} className="relative">
                  <button
                    onClick={(e) => handleDropdownClick(e, link.name)}
                    className={`flex items-center gap-1 text-sm font-medium ${textColor} hover:opacity-80 transition-colors`}
                    data-testid="nav-academics-dropdown"
                  >
                    {link.name}
                    <ChevronDown size={16} className={`transition-transform ${activeDropdown === link.name ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  <AnimatePresence>
                    {activeDropdown === link.name && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-2 w-80 bg-white shadow-xl border border-slate-100 rounded-sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="p-4">
                          {academicsDropdown.sections.map((section, sectionIndex) => (
                            <div key={section.title} className={sectionIndex > 0 ? 'mt-4 pt-4 border-t border-slate-100' : ''}>
                              <div className="flex items-center gap-2 mb-2">
                                <section.icon size={16} className="text-[#163A59]" />
                                <span className="text-[0.65rem] font-semibold uppercase tracking-[0.1em] text-slate-500">
                                  {section.title}
                                </span>
                              </div>
                              {section.items.map((item) => (
                                <Link
                                  key={item.path}
                                  to={item.path}
                                  onClick={() => setActiveDropdown(null)}
                                  className="block py-2 px-2 text-sm text-slate-700 hover:text-[#163A59] hover:bg-slate-50 rounded-sm transition-colors"
                                >
                                  {item.name}
                                </Link>
                              ))}
                            </div>
                          ))}
                          <div className="mt-4 pt-4 border-t border-slate-100">
                            <Link
                              to="/programs"
                              onClick={() => setActiveDropdown(null)}
                              className="block py-2 px-2 text-sm font-semibold text-[#163A59] hover:bg-[#163A59]/5 rounded-sm transition-colors"
                            >
                              View All Programs →
                            </Link>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium ${textColor} hover:opacity-80 transition-colors ${
                    location.pathname === link.path && (isScrolled || !isHomePage) ? 'text-[#163A59]' : ''
                  }`}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </nav>

          {/* Apply Now & LMS Buttons (Desktop) */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href={LMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-1.5 text-sm font-medium ${textColor} hover:opacity-80 transition-colors`}
              data-testid="header-lms-btn"
            >
              Student Portal
              <ExternalLink size={14} />
            </a>
            <Link
              to="/admissions"
              className="bg-[#C82032] hover:bg-[#A81828] text-white px-6 py-2.5 text-sm font-semibold tracking-wide transition-all"
              data-testid="header-apply-btn"
            >
              Apply Now
            </Link>
          </div>

          {/* Tablet compact CTA + Mobile Menu Button */}
          <div className="flex items-center gap-2 lg:hidden">
            <Link
              to="/admissions"
              className="hidden md:inline-flex bg-[#C82032] hover:bg-[#A81828] text-white px-5 py-2 text-sm font-semibold tracking-wide transition-all"
              data-testid="tablet-apply-btn"
            >
              Apply Now
            </Link>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 ${textColor}`}
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col px-6 py-4">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="py-3 text-sm font-medium text-slate-900 hover:text-[#163A59] transition-colors border-b border-slate-100"
              >
                Home
              </Link>
              <Link
                to="/about"
                onClick={() => setIsMobileMenuOpen(false)}
                className="py-3 text-sm font-medium text-slate-900 hover:text-[#163A59] transition-colors border-b border-slate-100"
              >
                About
              </Link>

              {/* Academics Section */}
              <div className="py-3 border-b border-slate-100">
                <p className="text-[0.65rem] font-semibold uppercase tracking-[0.1em] text-slate-500 mb-2">
                  Academics
                </p>
                <div className="pl-4 space-y-2">
                  <p className="text-xs font-semibold text-[#163A59] mt-2">Degree Programs</p>
                  <Link
                    to="/program/masters"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block py-1.5 text-sm text-slate-700 hover:text-[#163A59]"
                  >
                    Master of Humanitarian Action & Development
                  </Link>
                  <Link
                    to="/program/masters-ppm"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block py-1.5 text-sm text-slate-700 hover:text-[#163A59]"
                  >
                    Master&apos;s - Project &amp; Program Mgmt
                  </Link>
                  <Link
                    to="/program/doctoral"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block py-1.5 text-sm text-slate-700 hover:text-[#163A59]"
                  >
                    Doctoral Program
                  </Link>
                  
                  <p className="text-xs font-semibold text-[#163A59] mt-3">Certificate Courses</p>
                  <Link
                    to="/certificate/ai-humanitarian"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block py-1.5 text-sm text-slate-700 hover:text-[#163A59]"
                  >
                    AI in Humanitarian Action & Development
                  </Link>

                  <Link
                    to="/programs"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block py-1.5 text-sm font-semibold text-[#163A59]"
                  >
                    View All Programs →
                  </Link>
                </div>
              </div>

              <Link
                to="/scholarship"
                onClick={() => setIsMobileMenuOpen(false)}
                className="py-3 text-sm font-medium text-slate-900 hover:text-[#163A59] transition-colors border-b border-slate-100"
              >
                Scholarship
              </Link>
              <Link
                to="/admissions"
                onClick={() => setIsMobileMenuOpen(false)}
                className="py-3 text-sm font-medium text-slate-900 hover:text-[#163A59] transition-colors border-b border-slate-100"
              >
                Admissions
              </Link>
              <Link
                to="/faq"
                onClick={() => setIsMobileMenuOpen(false)}
                className="py-3 text-sm font-medium text-slate-900 hover:text-[#163A59] transition-colors border-b border-slate-100"
              >
                FAQ
              </Link>
              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="py-3 text-sm font-medium text-slate-900 hover:text-[#163A59] transition-colors border-b border-slate-100"
              >
                Contact
              </Link>
              <a
                href={LMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMobileMenuOpen(false)}
                className="py-3 text-sm font-medium text-slate-900 hover:text-[#163A59] transition-colors border-b border-slate-100 flex items-center gap-2"
                data-testid="mobile-nav-lms"
              >
                Student Portal (LMS)
                <ExternalLink size={14} />
              </a>
              <Link
                to="/admissions"
                onClick={() => setIsMobileMenuOpen(false)}
                className="mt-4 bg-[#C82032] hover:bg-[#A81828] text-white px-6 py-3 font-semibold text-center transition-all text-sm"
                data-testid="mobile-apply-btn"
              >
                Apply Now
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
